import {FC, useEffect, useMemo, useState} from 'react'
import {api, DoorCode, DoorCodeInput, ApiError} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {confirmDialog} from '../../components/dialog'
import {PageHead} from '../../components/PageHead'
import {ListToolbar} from '../../components/ListToolbar'
import {createPortal} from 'react-dom'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const DoorCodesPage: FC = () => {
  const toast = useToast()
  const [codes, setCodes] = useState<DoorCode[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [editCode, setEditCode] = useState<{c: DoorCode | null} | null>(null)
  const [search, setSearch] = useState('')

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return codes
    return codes.filter((c) => `${c.label} ${c.code} ${c.note ?? ''}`.toLowerCase().includes(q))
  }, [codes, search])

  const load = async () => {
    setLoading(true)
    try {
      setCodes(await api.adminDoorCodes())
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const delCode = async (c: DoorCode) => {
    if (!(await confirmDialog({title: `Delete "${c.label}"?`, text: 'Renters will no longer see this code.', confirmText: 'Delete', danger: true}))) return
    setBusyId(c.id)
    try {
      await api.deleteDoorCode(c.id)
      setCodes((l) => l.filter((x) => x.id !== c.id))
      toast.success('Door code deleted.')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not delete')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div>
      <PageHead icon='🔑' eyebrow='Content' title='Door Codes' sub='Publish entry codes that renters can see in their portal.'
        action={<button onClick={() => setEditCode({c: null})} style={btnHeader}>+ Add door code</button>} />

      {!loading && <ListToolbar search={search} onSearch={setSearch} placeholder='Search door codes…' />}

      {loading ? (
        <div style={{padding: 40, textAlign: 'center', color: MUTED}}>Loading…</div>
      ) : codes.length === 0 ? (
        <div className='ccp-fade-up' style={{background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', color: MUTED, boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>
          No door codes yet. Add your first one to share entry access with renters.
        </div>
      ) : shown.length === 0 ? (
        <div className='ccp-fade-up' style={{background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', color: MUTED, boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>
          No door codes match.
        </div>
      ) : (
        <div className='row g-3 ccp-stagger'>
          {shown.map((c) => (
            <div className='col-12 col-md-6 col-lg-4' key={c.id}>
              <div className='ccp-lift' style={{background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 6px 18px rgba(27,27,41,0.06)', height: '100%'}}>
                <div style={{fontSize: 13, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em'}}>{c.label}</div>
                <div style={{fontSize: 26, fontWeight: 800, color: ACCENT, letterSpacing: '0.04em', margin: '6px 0'}}>{c.code}</div>
                {c.note && <div style={{fontSize: 13, color: MUTED}}>{c.note}</div>}
                <div style={{display: 'flex', gap: 8, marginTop: 14}}>
                  <button onClick={() => setEditCode({c})} disabled={busyId === c.id} style={{...btnGhost, flex: 1}}>Edit</button>
                  <button onClick={() => delCode(c)} disabled={busyId === c.id} style={{...btnDanger, flex: 1}}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editCode && (
        <DoorCodeModal
          code={editCode.c}
          onClose={() => setEditCode(null)}
          onSaved={(saved, isNew) => {
            setCodes((l) => (isNew ? [...l, saved] : l.map((x) => (x.id === saved.id ? saved : x))))
            setEditCode(null)
            toast.success(`Door code ${isNew ? 'added' : 'saved'}.`)
          }}
        />
      )}
    </div>
  )
}

const DoorCodeModal: FC<{code: DoorCode | null; onClose: () => void; onSaved: (c: DoorCode, isNew: boolean) => void}> = ({code, onClose, onSaved}) => {
  const isNew = !code
  const [form, setForm] = useState<DoorCodeInput>({label: code?.label ?? '', code: code?.code ?? '', note: code?.note ?? '', sortOrder: code?.sortOrder ?? 0})
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    setErr(null)
    if (!form.label.trim()) return setErr('Label is required.')
    if (!form.code.trim()) return setErr('Code is required.')
    setBusy(true)
    try {
      const payload = {...form, note: form.note?.trim() || undefined}
      const saved = isNew ? await api.createDoorCode(payload) : await api.updateDoorCode(code!.id, payload)
      onSaved(saved, isNew)
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save')
      setBusy(false)
    }
  }

  return (
    <Modal title={isNew ? 'Add door code' : 'Edit door code'} onClose={onClose}>
      {err && <ErrBox>{err}</ErrBox>}
      <Field label='Label'><input value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} placeholder='e.g. Front door' style={inp} /></Field>
      <Field label='Code'><input value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} placeholder='e.g. 1234#' style={inp} /></Field>
      <Field label='Note (optional)'><input value={form.note ?? ''} onChange={(e) => setForm({...form, note: e.target.value})} placeholder='e.g. Main entrance' style={inp} /></Field>
      <Field label='Sort order'><input type='number' value={form.sortOrder ?? 0} onChange={(e) => setForm({...form, sortOrder: Number(e.target.value)})} style={inp} /></Field>
      <Footer onClose={onClose} onSave={save} busy={busy} saveLabel={isNew ? 'Add' : 'Save'} />
    </Modal>
  )
}

// ─── Small shared modal pieces ──────────────────────────────────────────
const Modal: FC<{title: string; onClose: () => void; children: React.ReactNode}> = ({title, onClose, children}) => createPortal(
  <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto'}}>
    <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, padding: '26px 24px', width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.3)'}}>
      <h2 style={{fontSize: 20, fontWeight: 800, color: INK, margin: '0 0 18px'}}>{title}</h2>
      {children}
    </div>
  </div>,
  document.body,
)
const Field: FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div style={{marginBottom: 14}}>
    <label style={{display: 'block', fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6}}>{label}</label>
    {children}
  </div>
)
const ErrBox: FC<{children: React.ReactNode}> = ({children}) => (
  <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 14}}>{children}</div>
)
const Footer: FC<{onClose: () => void; onSave: () => void; busy: boolean; saveLabel: string}> = ({onClose, onSave, busy, saveLabel}) => (
  <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18, paddingTop: 16, borderTop: '1px solid #f1f3f7'}}>
    <button onClick={onClose} style={btnGhost} disabled={busy}>Cancel</button>
    <button onClick={onSave} style={{...btnPrimary, minWidth: 110}} disabled={busy}>{busy ? <Spinner size={14} /> : saveLabel}</button>
  </div>
)

const btnBase: React.CSSProperties = {fontSize: 13, fontWeight: 600, borderRadius: 8, padding: '8px 14px', cursor: 'pointer', border: '1px solid transparent'}
const btnPrimary: React.CSSProperties = {...btnBase, background: ACCENT, color: '#fff'}
const btnHeader: React.CSSProperties = {...btnBase, background: '#fff', color: ACCENT, fontWeight: 700, padding: '10px 16px'}
const btnGhost: React.CSSProperties = {...btnBase, background: '#fff', border: '1px solid #e5e7eb', color: INK}
const btnDanger: React.CSSProperties = {...btnBase, background: 'rgba(217,33,78,0.1)', color: '#d9214e'}
const inp: React.CSSProperties = {width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, color: INK, background: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit'}

export {DoorCodesPage}
